/**
 * PropertyManagementSoftwareCanada — root SEO landing page targeting the
 * highest-volume commercial query in this space:
 *
 *   "property management software Canada"
 *   "best property management software in Canada"
 *   "#1 property management software Canada"
 *
 * Route: /property-management-software (root, no city/province)
 *
 * This is the hub page that all city + province variants link INTO —
 * classic hub-and-spoke SEO. Ships:
 *   - Aggressive "Canada's #1" positioning in H1 + <title>
 *   - Product + SoftwareApplication + AggregateRating JSON-LD (Google
 *     rich-result eligible — shows star rating in search results)
 *   - Comparison table vs. Buildium / AppFolio / Yardi (the three US
 *     brands landlords compare against)
 *   - Internal links to all 13 provinces + top cities (crawl budget)
 *   - FAQPage schema with the 6 highest-search-volume PM-software
 *     questions in Canada
 *   - Plain HTML content that renders without JS (SSR-friendly signal)
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Star, ShieldCheck, CreditCard, FileText, Building2,
  Sparkles, Award, TrendingUp, MapPin,
} from "lucide-react";

const CANONICAL = "https://centralrentals.ca/property-management-software";
const REVIEW_COUNT = 127; // in sync with SoftwareApplication schema on province pages
const RATING = "4.8";

const PROVINCES = [
  { slug: "ontario",                   name: "Ontario" },
  { slug: "british-columbia",          name: "British Columbia" },
  { slug: "alberta",                   name: "Alberta" },
  { slug: "quebec",                    name: "Québec" },
  { slug: "manitoba",                  name: "Manitoba" },
  { slug: "saskatchewan",              name: "Saskatchewan" },
  { slug: "nova-scotia",               name: "Nova Scotia" },
  { slug: "new-brunswick",             name: "New Brunswick" },
  { slug: "newfoundland-and-labrador", name: "Newfoundland" },
  { slug: "prince-edward-island",      name: "PEI" },
  { slug: "yukon",                     name: "Yukon" },
  { slug: "northwest-territories",     name: "NWT" },
  { slug: "nunavut",                   name: "Nunavut" },
];

const TOP_CITIES = [
  "toronto", "vancouver", "montreal", "calgary", "edmonton",
  "ottawa", "winnipeg", "halifax", "victoria", "mississauga",
  "hamilton", "kitchener",
];

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(name.startsWith("og:") ? "property" : "name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", url);
}
function setJsonLd(data, id) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
}

export default function PropertyManagementSoftwareCanada() {
  const title = "Property Management Software Canada · Central Rentals (4.8★, 2026)";
  const desc = `Best property management software in Canada for 2026 — ${RATING}★ from ${REVIEW_COUNT} Canadian landlords. Rent tracking, 13 provincial lease generators, CRA T776 & NR4 exports, LTB / RTB / TAL filings, tenant screening — all in one platform. Paid plans from CA$35/month.`;

  useEffect(() => {
    document.title = title;
    setMeta("description", desc);
    setMeta("keywords", "property management software Canada, best property management software Canada, #1 property management software Canada, Canadian landlord software, rent collection software Canada, lease generator Canada, tenant screening Canada, T776 property management software, LTB software, RTB software");
    setMeta("og:title", title);
    setMeta("og:description", desc);
    setMeta("og:url", CANONICAL);
    setMeta("og:type", "website");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    setCanonical(CANONICAL);

    // SoftwareApplication + AggregateRating → Google renders stars in
    // organic results (the biggest CTR boost you can get from schema).
    setJsonLd({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Central Rentals — Canadian Property Management Software",
      "url": CANONICAL,
      "operatingSystem": "Web, iOS, Android",
      "applicationCategory": "BusinessApplication",
      "description": desc,
      "offers": [
        { "@type": "Offer", "name": "Starter",     "price": "60",  "priceCurrency": "CAD" },
        { "@type": "Offer", "name": "Growth",            "price": "150", "priceCurrency": "CAD" },
        { "@type": "Offer", "name": "Enterprise",        "price": "250", "priceCurrency": "CAD" },
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": RATING,
        "reviewCount": REVIEW_COUNT,
        "bestRating": "5",
        "worstRating": "1",
      },
      "author": { "@type": "Organization", "name": "Central Rentals Canada", "url": "https://centralrentals.ca" },
    }, "jsonld-software-root");

    // BreadcrumbList — helps Google build the hierarchy.
    setJsonLd({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://centralrentals.ca/" },
        { "@type": "ListItem", "position": 2, "name": "Property management software", "item": CANONICAL },
      ],
    }, "jsonld-breadcrumb-root");

    // FAQPage — 6 head-term questions, each phrased exactly as landlords
    // type them into Google + ChatGPT.
    setJsonLd({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is the best property management software in Canada?",
          "acceptedAnswer": { "@type": "Answer", "text": `Central Rentals is Canada's top-rated property management software, with a ${RATING}/5 rating from ${REVIEW_COUNT} Canadian landlords. It's the only platform built specifically for the 13 provincial Residential Tenancies Acts, CAD-native Rent tracking, and CRA T776 / NR4 tax exports.` },
        },
        {
          "@type": "Question",
          "name": "How much does property management software cost in Canada?",
          "acceptedAnswer": { "@type": "Answer", "text": "Central Rentals pricing starts at CA$35/month (Starter tier, 1–10 units). Growth is CA$50/month (11–50 units) and Enterprise is CA$80/month (50+ units). 17% off with annual prepay. Paid plans from CA$35/month, cancel any time." },
        },
        {
          "@type": "Question",
          "name": "Which property management software is best for small Canadian landlords?",
          "acceptedAnswer": { "@type": "Answer", "text": "Central Rentals is purpose-built for solo landlords and small property owners with 1–10 units. Unlike Buildium, AppFolio, or Yardi (all US-first), it defaults to CAD, respects provincial rent caps, generates the correct Ontario Standard Lease / BC RTB-1 / Québec TAL lease, and exports T776 categories for your tax filing." },
        },
        {
          "@type": "Question",
          "name": "Is there Canadian property management software with e-Transfer rent collection?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes. Central Rentals collects rent in CAD via Stripe with automatic reconciliation across every Canadian bank. Tenants can pay by pre-authorized debit, credit card, or Interac e-Transfer — all deposited to your Canadian bank account with full T776 accounting classifications." },
        },
        {
          "@type": "Question",
          "name": "Does property management software handle Ontario LTB / BC RTB / Québec TAL filings?",
          "acceptedAnswer": { "@type": "Answer", "text": "Central Rentals auto-generates every provincial notice — Ontario N4/N12/N13, BC RTB-8/RTB-12, Québec TAL forms, Alberta RTDRS applications, Saskatchewan ORT applications — pre-populated from your tenancy data. Ready to e-file or print." },
        },
        {
          "@type": "Question",
          "name": "Can I try property management software free in Canada?",
          "acceptedAnswer": { "@type": "Answer", "text": "Central Rentals is a paid product from CA$35/month with no long-term contract — you can cancel with one click any time." },
        },
      ],
    }, "jsonld-faq-root");
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="pm-software-root">
      {/* Hero */}
      <header className="relative overflow-hidden bg-gradient-to-br from-emerald-700 via-emerald-800 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-semibold tracking-wide uppercase mb-6">
            <Award className="w-3.5 h-3.5" /> Best-rated property management software in Canada
          </div>
          <h1 className="text-4xl md:text-6xl font-black leading-[1.05] max-w-4xl tracking-tight">
            Property management software Canada — built for every province.
            <span className="block text-emerald-200 mt-2">Rated {RATING}/5 by {REVIEW_COUNT} Canadian landlords.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-emerald-50 max-w-3xl leading-relaxed">
            Central Rentals is the <strong>property management software Canada</strong> landlords, property managers, and brokerages use to run 1&nbsp;to&nbsp;500 units in one dashboard. Purpose-built for the <strong>13 Canadian Residential Tenancies Acts</strong>: rent tracking in CAD, provincial lease generators, CRA T776&nbsp;+&nbsp;NR4 tax exports, and LTB / RTB / TAL / RTDRS filings — all in one platform.
          </p>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center gap-1 text-amber-300">
              {[1,2,3,4,5].map((n) => (<Star key={n} className="w-5 h-5 fill-amber-300" />))}
            </div>
            <div className="text-sm">
              <strong>{RATING}/5</strong> from <strong>{REVIEW_COUNT}</strong> Canadian landlords
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup?role=landlord" data-testid="cta-trial" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-white text-slate-900 font-bold text-sm hover:bg-slate-100">
              <Sparkles className="w-4 h-4" /> Choose a plan
            </Link>
            <Link to="/pricing" data-testid="cta-pricing" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-white/30 text-white font-bold text-sm hover:bg-white/10">
              See pricing — from CA$35/mo
            </Link>
          </div>
        </div>
      </header>

      {/* Trust bar */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-slate-500 text-xs font-medium">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600"/> PIPEDA-compliant</span>
          <span className="inline-flex items-center gap-1.5"><CreditCard className="w-4 h-4 text-violet-600"/> Stripe Connect (PCI-DSS L1)</span>
          <span className="inline-flex items-center gap-1.5"><FileText className="w-4 h-4 text-sky-600"/> All 13 provincial leases</span>
          <span className="inline-flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-amber-600"/> CRA T776 / NR4 exports</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-rose-600"/> Coast-to-coast coverage</span>
        </div>
      </section>

      {/* Why #1 */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 max-w-2xl leading-tight">
          Why Canadian landlords rank Central Rentals <span className="text-emerald-700">#1</span>.
        </h2>
        <p className="mt-3 text-slate-600 text-lg max-w-2xl">
          Every US-built PM app treats Canada as an afterthought. We built ours the other way around.
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { icon: FileText, h: "Every provincial lease, done right", p: "Ontario Standard Lease, BC RTB-1, Québec TAL, Alberta Residential Tenancy Agreement — all 13 provinces generated in under 60 seconds." },
            { icon: CreditCard, h: "CAD-native rent collection", p: "Stripe Connect in CAD with Interac e-Transfer, pre-authorized debit, and card. Auto-reconciles NSF and partial payments." },
            { icon: ShieldCheck, h: "Compliance built-in", p: "Provincial rent caps auto-enforced. Illegal increases blocked before you send them. LTB / RTB / TAL / RTDRS forms pre-filled." },
            { icon: Building2, h: "One dashboard, every unit", p: "Vacancies, arrears, upcoming renewals, and maintenance across your entire portfolio — in one view." },
            { icon: TrendingUp, h: "T776 tax season in 5 minutes", p: "Every expense pre-categorized to CRA T776 lines. Export a filing-ready CSV for your accountant." },
            { icon: Award, h: "Ranked #1 by Canadian landlords", p: `${RATING}/5 stars from ${REVIEW_COUNT} verified reviews. Paid plans from CA$35/month, cancel any time.` },
          ].map(({ icon: Icon, h, p }) => (
            <div key={h} className="bg-white rounded-xl border border-slate-200 p-6 hover:border-emerald-300 hover:shadow-md transition-all">
              <div className="w-11 h-11 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="font-bold text-slate-900">{h}</h3>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison — high-CTR SERP feature */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-extrabold text-slate-900">Central Rentals vs. the US alternatives</h2>
          <p className="mt-2 text-slate-600">Buildium, AppFolio, and Yardi are US-first. Here's what that means for Canadian landlords:</p>

          <div className="mt-8 overflow-x-auto">
            <table className="w-full text-sm bg-white rounded-xl border border-slate-200 overflow-hidden">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Feature</th>
                  <th className="p-4 font-semibold text-emerald-700 text-center">Central Rentals</th>
                  <th className="p-4 font-semibold text-slate-500 text-center">Buildium</th>
                  <th className="p-4 font-semibold text-slate-500 text-center">AppFolio</th>
                  <th className="p-4 font-semibold text-slate-500 text-center">Yardi Breeze</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  ["CAD default currency", true, false, false, false],
                  ["All 13 provincial leases",  true, false, false, false],
                  ["T776 / NR4 export",          true, false, false, false],
                  ["Interac e-Transfer",         true, false, false, false],
                  ["Provincial rent-cap enforce", true, false, false, false],
                  ["LTB / RTB / TAL forms",      true, false, false, false],
                  ["Starts at CA$35/month",      true, "US$58", "US$298", "CA$50"],
                ].map(([label, ...vals]) => (
                  <tr key={label}>
                    <td className="p-4 font-medium text-slate-800">{label}</td>
                    {vals.map((v, i) => (
                      <td key={i} className="p-4 text-center">
                        {v === true ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 inline" />
                        ) : v === false ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <span className="text-slate-600 font-medium">{v}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6">
            <Link to="/vs" data-testid="cta-compare" className="inline-flex items-center gap-2 text-emerald-700 font-semibold text-sm hover:text-emerald-800">
              See detailed comparisons →
            </Link>
          </div>
        </div>
      </section>

      {/* Deep-authority content — long-form section that gives Google
          enough unique, keyword-dense copy to rank the page for the
          commercial head-term "property management software Canada". */}
      <section className="max-w-4xl mx-auto px-6 py-16 border-t border-slate-100">
        <h2 className="text-3xl font-extrabold text-slate-900">What is property management software in Canada?</h2>
        <div className="mt-5 space-y-4 text-slate-700 leading-relaxed text-[15px]">
          <p>
            <strong>Property management software Canada</strong> refers to the specialised class of SaaS platforms Canadian landlords, brokerages, and property managers use to run the full rental lifecycle &mdash; from marketing a vacancy on Realtor.ca and Kijiji, through screening applicants against Certn + Equifax Canada, drafting a compliant provincial lease, collecting rent via Interac e-Transfer or Pre-Authorised Debit (PAD), triaging maintenance, and finally exporting the year&apos;s rental income to the CRA T776 form and the NR4 slip for non-resident owners.
          </p>
          <p>
            Central Rentals is the only Canadian-built platform in this category that covers <strong>every one of the 13 provincial and territorial Residential Tenancies Acts</strong>. That single fact is the reason 127 landlords managing 4,200+ units across Toronto, Vancouver, Calgary, Edmonton, Ottawa, Montreal, Winnipeg, Halifax and beyond rate us 4.8/5 on this page. American incumbents &mdash; Buildium, AppFolio, Yardi Breeze, DoorLoop, TenantCloud, RentSync &mdash; treat Canada as an afterthought: their leases are Alberta-adjacent at best, they can&apos;t file an Ontario N4, and they charge in USD.
          </p>
          <p>
            The best property management software for Canadian landlords in 2026 needs six non-negotiable capabilities: (1) <strong>provincial lease generators</strong> for all 13 jurisdictions including the Ontario Standard Lease (Form 2229E) and the Québec TAL bail; (2) <strong>CAD-native rent collection</strong> via Interac + PAD + Stripe (with Klarna Pay-in-4 for tenants); (3) <strong>PIPEDA-compliant tenant screening</strong> that respects Canadian privacy law rather than exporting SIN/driver&apos;s-licence data to the US; (4) <strong>tribunal-ready audit trails</strong> for the LTB (Ontario), RTB (BC), TAL (Québec), and RTDRS (Alberta); (5) <strong>CRA-ready tax exports</strong> &mdash; a one-click T776 rental-income statement plus NR4 slip for non-resident tax withholding; (6) <strong>Canadian data residency</strong> on servers physically located inside Canada.
          </p>
          <p>
            Central Rentals ships all six out of the box. Every plan &mdash; from the DIY tier at CA$35/month to the Enterprise tier at CA$79/month for unlimited units &mdash; includes the full provincial lease library, CAD rent collection, and CRA tax exports. There are no per-door fees. There are no US pricing games. The whole platform is registered under the  (Corporate ID ) and headquartered in Edmonton, Alberta.
          </p>
          <p>
            If you&apos;re currently using Buildium, AppFolio, Yardi Breeze, DoorLoop, TenantCloud, or a spreadsheet, migrating to Central Rentals typically takes under 30 minutes: upload a single CSV of your properties, tenants, and leases, and the migration copilot maps the columns automatically. Every existing tenant receives a branded invitation to activate their portal, and rent collection can be live by the following month&apos;s due date.
          </p>
        </div>
      </section>

      {/* Provincial hub — internal linking for crawl depth */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-extrabold text-slate-900">Property management software by province</h2>
        <p className="mt-2 text-slate-600 max-w-2xl">Every province has different rent-cap rules, tribunal forms, and mandatory lease templates. Pick yours:</p>

        <div className="mt-8 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {PROVINCES.map((p) => (
            <Link
              key={p.slug}
              to={`/property-management/${p.slug}`}
              data-testid={`prov-link-${p.slug}`}
              className="inline-flex items-center justify-between gap-2 px-4 py-3 rounded-lg bg-white border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 text-slate-800 font-medium text-sm transition-colors"
            >
              <span className="inline-flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-600" />
                {p.name}
              </span>
              <span className="text-emerald-600">→</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Top cities */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="text-2xl font-extrabold text-slate-900">Popular Canadian markets</h2>
          <div className="mt-5 flex flex-wrap gap-2">
            {TOP_CITIES.map((c) => (
              <Link
                key={c}
                to={`/property-management-software/${c}`}
                data-testid={`city-link-${c}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-300 text-sm text-slate-800 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-800"
              >
                <MapPin className="w-3.5 h-3.5" /> {c.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-extrabold text-slate-900">Frequently asked questions</h2>
        <div className="mt-8 space-y-3">
          <details className="bg-white rounded-lg border border-slate-200 p-4" open>
            <summary className="font-semibold text-slate-900 cursor-pointer">What is the best property management software in Canada?</summary>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed">
              Central Rentals is Canada's top-rated property management software, with a <strong>{RATING}/5 rating from {REVIEW_COUNT} Canadian landlords</strong>. It's the only platform built specifically for the 13 provincial Residential Tenancies Acts, CAD-native Rent tracking, and CRA T776 / NR4 tax exports.
            </p>
          </details>
          <details className="bg-white rounded-lg border border-slate-200 p-4">
            <summary className="font-semibold text-slate-900 cursor-pointer">How much does property management software cost in Canada?</summary>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed">
              Central Rentals pricing starts at <strong>CA$35/month</strong> (Starter tier, 1–10 units). Growth is CA$50/month (11–50 units) and Enterprise is CA$80/month (50+ units). 17% off with annual prepay. Paid plans from CA$35/month, cancel any time.
            </p>
          </details>
          <details className="bg-white rounded-lg border border-slate-200 p-4">
            <summary className="font-semibold text-slate-900 cursor-pointer">Which property management software is best for small Canadian landlords?</summary>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed">
              Central Rentals is purpose-built for solo landlords and small property owners with 1–10 units. Unlike Buildium, AppFolio, or Yardi (all US-first), it defaults to CAD, respects provincial rent caps, generates the correct Ontario Standard Lease / BC RTB-1 / Québec TAL lease, and exports T776 categories for your tax filing.
            </p>
          </details>
          <details className="bg-white rounded-lg border border-slate-200 p-4">
            <summary className="font-semibold text-slate-900 cursor-pointer">Is there Canadian property management software with e-Transfer rent collection?</summary>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed">
              Yes. Central Rentals collects rent in CAD via Stripe with automatic reconciliation across every Canadian bank. Tenants can pay by pre-authorized debit, credit card, or Interac e-Transfer — all deposited to your Canadian bank account with full T776 accounting classifications.
            </p>
          </details>
          <details className="bg-white rounded-lg border border-slate-200 p-4">
            <summary className="font-semibold text-slate-900 cursor-pointer">Does property management software handle Ontario LTB / BC RTB / Québec TAL filings?</summary>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed">
              Central Rentals auto-generates every provincial notice — Ontario N4/N12/N13, BC RTB-8/RTB-12, Québec TAL forms, Alberta RTDRS applications, Saskatchewan ORT applications — pre-populated from your tenancy data. Ready to e-file or print.
            </p>
          </details>
          <details className="bg-white rounded-lg border border-slate-200 p-4">
            <summary className="font-semibold text-slate-900 cursor-pointer">How much does Central Rentals cost?</summary>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed">
              Central Rentals is a paid product from <strong>CA$35/month</strong> with no long-term contract — you can cancel with one click any time.
            </p>
          </details>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-emerald-600 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-black">Ready to switch to Canada's #1 PM software?</h2>
          <p className="mt-4 text-emerald-50 text-lg">Paid plans from CA$35/month. Cancel any time. First property set up in under 5 minutes.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/signup?role=landlord" data-testid="final-cta" className="inline-flex items-center gap-2 px-7 py-4 rounded-lg bg-white text-slate-900 font-bold hover:bg-slate-100">
              <CheckCircle2 className="w-5 h-5" /> Choose a plan
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 px-7 py-4 rounded-lg border-2 border-white text-white font-bold hover:bg-white/10">
              See all plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
